import { inject } from "@angular/core";
import { CanActivateFn, Router } from "@angular/router";
import { TranslateService } from "@policystreet/localization";
import { ClaimsService } from "../_services/claims.service";
import { catchError, map, of, switchMap } from "rxjs";
import { INSURANCE_IDS_ROUTES } from "../../../shared/constants/insurance-ids.constant";
import { openClaimRoute } from "../_functions/open-claim-route.function";
import { AlertService } from "../../../shared/services/alert.service";
import { ProductCode } from "../../../shared/constants/product-code.constant";
import { MARKET } from "../../../shared/market/market.token";

export const tokenHandlerGuard: CanActivateFn = (route, state) => {
    const alertService = inject(AlertService);
    const claimsService = inject(ClaimsService);
    const router = inject(Router);
    const translate = inject(TranslateService);
    const market = inject(MARKET);
    const token = route.queryParamMap.get("token");

    if (!token) {
        router.navigate(["/service/invalid-token"]);
        localStorage.removeItem("claim_token_data");
        sessionStorage.removeItem("claim_id_token");
        return of(false);
    }

    return claimsService.decodeToken$(token).pipe(
        switchMap((res) => {
            const prefixRoute =
                INSURANCE_IDS_ROUTES[res.insuranceProviderId.toLowerCase() as keyof typeof INSURANCE_IDS_ROUTES];

            return claimsService.getClaimByPipoClaimId$(res.pipoClaimId).pipe(
                map((claim) => {
                    // Path 1: no existing claim
                    if (!claim.claimId || !claim.status) {
                        // Markets with a choice page (ID) let the customer pick Repair or Guarantee Buyback first.
                        if (market.landing === "choice") {
                            void router.navigate(["/claims/choose", res.extTrackId]);
                            return false;
                        }

                        const claimsBasePath =
                            res.productCode === ProductCode.AP ? "/allergy-claims" : `${prefixRoute}/claims`;
                        void router.navigate([claimsBasePath, res.extTrackId], {
                            state: { skipClaimCheck: true },
                        });
                        return false;
                    }

                    // Paths 2 and 3: an ongoing claim. Repair PC -> supplement documents, other Repair ->
                    // success, Guarantee Buyback (any ongoing status) -> its own success page.
                    const target = openClaimRoute(claim.claimId, claim.status, claim.claimType);
                    if (target) {
                        void router.navigate(target);
                        return false;
                    }

                    // Path 4: existing claim in non-ongoing status (OR || IR || TR || C || D)
                    alertService.openSnackBar(translate.instant("ui.duplicatePipoClaimIdDetected"));
                    void router.navigate(["/service/invalid-token"]);
                    return false;
                }),
            );
        }),
        catchError(() => {
            void router.navigate(["/service/invalid-token"]);
            localStorage.removeItem("claim_token_data");
            sessionStorage.removeItem("claim_id_token");
            return of(false);
        }),
    );
};
